import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import {
  Store, Search, ShoppingCart, Heart, Menu, Star, Truck, Shield, Headphones,
  Loader2, GraduationCap, Crown, Briefcase, Package, ChevronRight,
} from 'lucide-react';

/**
 * Self-contained template preview renderer.
 *
 * Renders a representative storefront mockup using ONLY the template definition
 * (colors, pages, modules) plus placeholder products — so ANY template (built-in
 * or custom) can be previewed without a live demo tenant existing. This fixes
 * "Preview Live" for custom templates that have no seeded demo store.
 */

const DUMMY_PRODUCTS = [
  { name: 'Produk Unggulan A', price: 149000, sale: 119000, img: 'prod1' },
  { name: 'Produk Best Seller B', price: 259000, sale: null, img: 'prod2' },
  { name: 'Produk Terlaris C', price: 89000, sale: 69000, img: 'prod3' },
  { name: 'Produk Premium D', price: 499000, sale: 429000, img: 'prod4' },
  { name: 'Produk Populer E', price: 199000, sale: null, img: 'prod5' },
  { name: 'Produk Diskon F', price: 349000, sale: 299000, img: 'prod6' },
  { name: 'Produk Baru G', price: 129000, sale: null, img: 'prod7' },
  { name: 'Produk Favorit H', price: 279000, sale: 239000, img: 'prod8' },
];

const DUMMY_COURSES = [
  { name: 'Kelas Digital Marketing', price: 350000, instructor: 'Rina P.', img: 'course1' },
  { name: 'Bootcamp UI/UX 30 Hari', price: 1499000, instructor: 'Ahmad F.', img: 'course2' },
  { name: 'Master Excel Bisnis', price: 199000, instructor: 'Dewi L.', img: 'course3' },
];

const DUMMY_SERVICES = [
  { name: 'Jasa Konsultasi Bisnis', price: 5000000, desc: 'Sesi 1-on-1 dengan expert' },
  { name: 'Pembuatan Website Custom', price: 15000000, desc: 'Full-stack, SEO ready' },
  { name: 'Paket Branding Lengkap', price: 8000000, desc: 'Logo, guideline, aset' },
];

const fmt = (n) => 'Rp ' + (n || 0).toLocaleString('id-ID');

export default function TemplatePreview() {
  const { templateId } = useParams();
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [schemeIdx, setSchemeIdx] = useState(0);

  useEffect(() => {
    document.title = 'Preview Template';
    api.get(`/public/template-preview/${templateId}`)
      .then((res) => setTpl(res.data.template))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [templateId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !tpl) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-3 px-4 text-center">
        <Store className="w-12 h-12 text-slate-300" />
        <h1 className="text-xl font-bold text-slate-700">Template tidak ditemukan</h1>
        <p className="text-sm text-slate-500">Template yang ingin dipreview tidak tersedia atau sudah dihapus.</p>
      </div>
    );
  }

  const schemes = tpl.color_schemes?.length ? tpl.color_schemes : [{ name: 'Default', primary: '#2D6CDF', secondary: '#1e293b', accent: '#10b981' }];
  const scheme = schemes[schemeIdx] || schemes[0];
  const primary = scheme.primary || '#2D6CDF';
  const secondary = scheme.secondary || '#1e293b';
  const accent = scheme.accent || '#10b981';

  const modules = tpl.default_modules || [];
  const pages = tpl.default_pages || [];
  const category = tpl.category || 'commerce';

  const has = (m) => modules.includes(m);
  // Layout detection — category wins; module heuristics only break ties for ambiguous
  // categories ('other'/'custom'). A template with product modules defaults to commerce
  // even if it also enables course/service modules (common when admin checks everything).
  const hasProducts = has('products_physical') || has('products_digital') || has('checkout');
  let isCourse = category === 'education';
  let isService = category === 'services';
  if (!isCourse && !isService && !hasProducts) {
    if (has('lms') || has('membership')) isCourse = true;
    else if (has('high_ticket') || has('payment_plan')) isService = true;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Preview banner */}
      <div className="text-white text-center py-2 text-xs font-medium" style={{ background: secondary }}>
        🎨 MODE PREVIEW TEMPLATE — <strong>{tpl.name}</strong> · Ini tampilan contoh dengan data dummy
      </div>

      {/* Color scheme switcher */}
      {schemes.length > 1 && (
        <div className="flex items-center justify-center gap-2 py-2 bg-slate-50 border-b border-slate-100 flex-wrap">
          <span className="text-xs text-slate-500">Skema warna:</span>
          {schemes.map((s, i) => (
            <button
              key={i}
              onClick={() => setSchemeIdx(i)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs border transition-all ${i === schemeIdx ? 'border-slate-400 bg-white shadow-sm' : 'border-transparent hover:bg-white'}`}
            >
              <span className="w-3 h-3 rounded-full" style={{ background: s.primary }} />
              {s.name || `Skema ${i + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2 font-bold text-lg" style={{ color: primary }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: primary }}>
              {isCourse ? <GraduationCap size={18} /> : isService ? <Briefcase size={18} /> : <Store size={18} />}
            </div>
            {tpl.name}
          </div>
          <div className="flex-1 hidden md:flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
            <Search size={16} className="text-slate-400" />
            <span className="text-sm text-slate-400">Cari produk...</span>
          </div>
          <div className="flex items-center gap-3 text-slate-500">
            <Heart size={20} />
            <ShoppingCart size={20} />
            <Menu size={20} className="md:hidden" />
          </div>
        </div>
        {/* Nav from template pages */}
        {pages.length > 0 && (
          <nav className="border-t border-slate-50 bg-slate-50/50">
            <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-5 overflow-x-auto text-sm">
              {pages.slice(0, 8).map((p, i) => (
                <span key={i} className={`whitespace-nowrap ${i === 0 ? 'font-semibold' : 'text-slate-600'}`} style={i === 0 ? { color: primary } : {}}>
                  {p.title}
                </span>
              ))}
            </div>
          </nav>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}>
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-24 text-white">
          <div className="max-w-xl">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: accent }}>
              {isCourse ? '🎓 Platform Belajar Online' : isService ? '💼 Layanan Profesional' : '🛍️ Belanja Online'}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
              {isCourse ? 'Tingkatkan Skill Anda Sekarang' : isService ? 'Solusi Bisnis Terpercaya' : 'Temukan Produk Terbaik'}
            </h1>
            <p className="text-white/80 mb-6 text-lg">{tpl.description || 'Template toko online dengan tampilan modern dan lengkap.'}</p>
            <div className="flex gap-3">
              <button className="px-6 py-3 rounded-lg font-semibold text-white shadow-lg" style={{ background: accent }}>
                {isCourse ? 'Mulai Belajar' : isService ? 'Konsultasi Gratis' : 'Belanja Sekarang'}
              </button>
              <button className="px-6 py-3 rounded-lg font-semibold bg-white/10 backdrop-blur border border-white/20">
                Lihat Katalog
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { icon: Truck, label: 'Pengiriman Cepat' },
            { icon: Shield, label: 'Pembayaran Aman' },
            { icon: Headphones, label: 'CS 24/7' },
            { icon: Star, label: 'Rating Terbaik' },
          ].map((b, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <b.icon size={22} style={{ color: primary }} />
              <span className="text-xs text-slate-600">{b.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Main content — varies by category */}
      {isCourse ? (
        <Section title="Kursus Populer" primary={primary}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {DUMMY_COURSES.map((c, i) => (
              <div key={i} className="rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
                <img src={`https://picsum.photos/seed/${c.img}/600/340`} alt="" className="w-full h-40 object-cover" />
                <div className="p-4">
                  <h3 className="font-semibold text-slate-900 mb-1">{c.name}</h3>
                  <p className="text-xs text-slate-500 mb-3">Instruktur: {c.instructor}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold" style={{ color: primary }}>{fmt(c.price)}</span>
                    <button className="text-xs px-3 py-1.5 rounded-md text-white" style={{ background: accent }}>Daftar</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      ) : isService ? (
        <Section title="Layanan Kami" primary={primary}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {DUMMY_SERVICES.map((s, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white mb-4" style={{ background: primary }}>
                  <Briefcase size={22} />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{s.name}</h3>
                <p className="text-sm text-slate-500 mb-4">{s.desc}</p>
                <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                  <div>
                    <p className="text-[10px] text-slate-400">Mulai dari</p>
                    <span className="font-bold" style={{ color: primary }}>{fmt(s.price)}</span>
                  </div>
                  <button className="text-xs px-3 py-1.5 rounded-md text-white flex items-center gap-1" style={{ background: accent }}>
                    Pesan <ChevronRight size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      ) : (
        <Section title="Produk Terlaris" primary={primary}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {DUMMY_PRODUCTS.map((p, i) => (
              <div key={i} className="rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow group">
                <div className="relative">
                  <img src={`https://picsum.photos/seed/${p.img}/400/400`} alt="" className="w-full aspect-square object-cover" />
                  {p.sale && (
                    <span className="absolute top-2 left-2 text-white text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: accent }}>
                      -{Math.round((1 - p.sale / p.price) * 100)}%
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-medium text-slate-900 line-clamp-2 mb-1">{p.name}</h3>
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map((s) => <Star key={s} size={11} className="fill-amber-400 text-amber-400" />)}
                    <span className="text-[10px] text-slate-400">(120)</span>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-bold" style={{ color: primary }}>{fmt(p.sale || p.price)}</span>
                    {p.sale && <span className="text-[11px] text-slate-400 line-through">{fmt(p.price)}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Module-driven feature strip */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <h2 className="text-center text-xl font-bold text-slate-900 mb-2">Fitur Toko Ini</h2>
          <p className="text-center text-sm text-slate-500 mb-6">Modul yang aktif di template <strong>{tpl.name}</strong></p>
          <div className="flex flex-wrap justify-center gap-2">
            {modules.map((m, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-slate-200 flex items-center gap-1.5">
                <Package size={12} style={{ color: primary }} /> {m.replace(/_/g, ' ')}
              </span>
            ))}
            {modules.length === 0 && <span className="text-sm text-slate-400">Belum ada modul aktif</span>}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white" style={{ background: secondary }}>
        <div className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <div className="col-span-2 md:col-span-1">
            <div className="font-bold text-lg mb-2">{tpl.name}</div>
            <p className="text-white/60 text-xs">{tpl.description}</p>
          </div>
          {['Produk', 'Bantuan', 'Perusahaan'].map((col) => (
            <div key={col}>
              <p className="font-semibold mb-2">{col}</p>
              <ul className="space-y-1 text-white/60 text-xs">
                <li>Link 1</li><li>Link 2</li><li>Link 3</li>
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 py-4 text-center text-white/50 text-xs">
          Preview Template — Dibuat dengan Tokomoe
        </div>
      </footer>
    </div>
  );
}

function Section({ title, primary, children }) {
  return (
    <section className="max-w-6xl mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <span className="w-1 h-6 rounded-full" style={{ background: primary }} />
          {title}
        </h2>
        <button className="text-sm flex items-center gap-1" style={{ color: primary }}>
          Lihat Semua <ChevronRight size={14} />
        </button>
      </div>
      {children}
    </section>
  );
}
